import { checkAccountHierarchyDepth } from "@common/constants/domains/accounting/accountGroups";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { isSameClassification } from "@common/constants/domains/accounting/accountTypeHelpers";
import { uniq } from "@common/helpers/collections";
import { RowDropPosition } from "@design-system/ViewTable/ViewTable.lib";
import { MoveInsertPosition } from "../draftMoveOperations";
import { DraftAccount, DraftGroup } from "../draftState";
import { GetRowCapabilities } from "../editorCapabilities";
import {
    collectSubtreeGroupIds,
    findGroupInScope,
} from "../draftGroupOperations";
import { resolveCustomGroupChain } from "../groupChain";
import { normalizeGroupName } from "../draftOperations";
import { NestedEditorRow } from "./nestedRowTypes";

export type DropBlockReason =
    | "differentType"
    | "lockedDescendant"
    | "depthExceeded"
    | "cycle"
    | "duplicateName";

export type DropEvaluation =
    | { ok: true }
    | { ok: false; reason: DropBlockReason };

// entityId scopes duplicate-group-name to one business.
export interface DropScope {
    entityId: number;
    type: AccountType;
    parentGroupId: string | null;
}

export const isAccountInDropScope = (
    account: Pick<DraftAccount, "entityId" | "type" | "parentGroupId">,
    targetScope: DropScope,
): boolean =>
    account.entityId === targetScope.entityId &&
    account.type === targetScope.type &&
    (account.parentGroupId ?? null) === (targetScope.parentGroupId ?? null);

interface DropTarget {
    scope: DropScope;
    overRefId: string | null;
    position: MoveInsertPosition;
}

// Report-leaf folding is presentation-only; drag scopes use exact types.
const sameType = (a: AccountType, b: AccountType): boolean => a === b;

export const parentChainDepth = (
    parentGroupId: string | null,
    groupsById: Map<string, DraftGroup>,
): number =>
    parentGroupId == null
        ? 0
        : resolveCustomGroupChain(parentGroupId, groupsById).ids.length;

// Visited-set guards a parentGroupId cycle the editor cannot produce.
const subtreeGroupDepth = (groupId: string, groups: DraftGroup[]): number => {
    const visit = (id: string, visited: Set<string>): number => {
        if (visited.has(id)) {
            return 0;
        }
        visited.add(id);
        const children = groups.filter((group) => group.parentGroupId === id);
        if (children.length === 0) {
            return 1;
        }
        return (
            1 + Math.max(...children.map((child) => visit(child.id, visited)))
        );
    };
    return visit(groupId, new Set());
};

export const isDescendantOrSelf = (
    candidateId: string,
    ancestorId: string,
    groupsById: Map<string, DraftGroup>,
): boolean => {
    const visited = new Set<string>();
    let currentId: string | null = candidateId;
    while (currentId) {
        if (currentId === ancestorId) {
            return true;
        }
        if (visited.has(currentId)) {
            break;
        }
        visited.add(currentId);
        currentId = groupsById.get(currentId)?.parentGroupId ?? null;
    }
    return false;
};

// Deprecated types: treat as within limit rather than throw.
const isWithinDepth = (type: AccountType, chainDepth: number): boolean => {
    try {
        return checkAccountHierarchyDepth({
            type,
            accountGroupChainDepth: chainDepth,
        }).isWithinLimit;
    } catch {
        return true;
    }
};

interface EvaluateDropParams {
    draggedRow: NestedEditorRow;
    targetScope: DropScope;
    groups: DraftGroup[];
    getRowCapabilities: GetRowCapabilities;
    accounts: DraftAccount[];
    // Depth of a parent that will be created from a name path.
    destinationChainDepth?: number;
    // Created parent is empty; a same-named root sibling is not a collision.
    skipDuplicateName?: boolean;
}

export const hasAccountNameSiblingCollision = ({
    targetScope,
    movingAccount,
    excludeAccountIds,
    accounts,
}: {
    targetScope: DropScope;
    movingAccount: DraftAccount;
    excludeAccountIds: ReadonlySet<string>;
    accounts: DraftAccount[];
}): boolean => {
    const movingName = normalizeGroupName(movingAccount.name);
    if (movingName.length === 0) {
        return false;
    }
    return accounts.some(
        (account) =>
            !excludeAccountIds.has(account.id) &&
            isAccountInDropScope(account, targetScope) &&
            normalizeGroupName(account.name) === movingName,
    );
};

const evaluateAccountDrop = ({
    draggedRow,
    targetScope,
    groupsById,
    getRowCapabilities,
    destinationChainDepth,
    accounts,
    skipDuplicateName,
}: {
    draggedRow: NestedEditorRow;
    targetScope: DropScope;
    groupsById: Map<string, DraftGroup>;
    getRowCapabilities: GetRowCapabilities;
    destinationChainDepth?: number;
    accounts: DraftAccount[];
    skipDuplicateName?: boolean;
}): DropEvaluation => {
    const account = draggedRow.account;
    if (!account) {
        return { ok: true };
    }
    const isTypeLocked =
        getRowCapabilities(account).typeChangeBlockReason != null;
    if (isTypeLocked) {
        if (!sameType(account.type, targetScope.type)) {
            return { ok: false, reason: "differentType" };
        }
    } else if (!isSameClassification(account.type, targetScope.type)) {
        return { ok: false, reason: "differentType" };
    }
    const chainDepth =
        destinationChainDepth ??
        parentChainDepth(targetScope.parentGroupId, groupsById);
    if (!isWithinDepth(targetScope.type, chainDepth)) {
        return { ok: false, reason: "depthExceeded" };
    }
    if (!skipDuplicateName) {
        const movingAccount =
            draggedRow.accounts.find(
                (constituent) => constituent.entityId === targetScope.entityId,
            ) ?? account;
        // A same-parent reorder keeps the uniqueness key, so a pre-existing
        // duplicate may still be reordered.
        if (
            !isAccountInDropScope(movingAccount, targetScope) &&
            hasAccountNameSiblingCollision({
                targetScope,
                movingAccount,
                excludeAccountIds: new Set(
                    draggedRow.accounts.map((constituent) => constituent.id),
                ),
                accounts,
            })
        ) {
            return { ok: false, reason: "duplicateName" };
        }
    }
    return { ok: true };
};

const evaluateGroupDrop = ({
    draggedRow,
    targetScope,
    groups,
    groupsById,
    accounts,
    getRowCapabilities,
    destinationChainDepth,
    skipDuplicateName,
}: {
    draggedRow: NestedEditorRow;
    targetScope: DropScope;
    groups: DraftGroup[];
    groupsById: Map<string, DraftGroup>;
    accounts: DraftAccount[];
    getRowCapabilities: GetRowCapabilities;
    destinationChainDepth?: number;
    skipDuplicateName?: boolean;
}): DropEvaluation => {
    const group = draggedRow.group;
    if (!group) {
        return { ok: true };
    }
    if (!isSameClassification(group.type, targetScope.type)) {
        return { ok: false, reason: "differentType" };
    }
    if (!sameType(group.type, targetScope.type)) {
        // Every constituent's subtree: a merged row's group is only the seed
        // business, and a locked descendant can live under any of them.
        const subtreeGroupIds = new Set<string>();
        for (const constituent of draggedRow.groups) {
            for (const id of collectSubtreeGroupIds(constituent.id, groups)) {
                subtreeGroupIds.add(id);
            }
        }
        const hasLockedDescendant = accounts.some(
            (account) =>
                account.parentGroupId != null &&
                subtreeGroupIds.has(account.parentGroupId) &&
                getRowCapabilities(account).typeChangeBlockReason != null &&
                !sameType(account.type, targetScope.type),
        );
        if (hasLockedDescendant) {
            return { ok: false, reason: "lockedDescendant" };
        }
    }
    if (
        targetScope.parentGroupId != null &&
        isDescendantOrSelf(targetScope.parentGroupId, group.id, groupsById)
    ) {
        return { ok: false, reason: "cycle" };
    }
    const parentDepth =
        destinationChainDepth ??
        parentChainDepth(targetScope.parentGroupId, groupsById);
    const deepestChainDepth = parentDepth + subtreeGroupDepth(group.id, groups);
    if (!isWithinDepth(group.type, deepestChainDepth)) {
        return { ok: false, reason: "depthExceeded" };
    }
    if (!skipDuplicateName) {
        const duplicate = findGroupInScope({
            groups: groups.filter((candidate) => candidate.id !== group.id),
            entityId: targetScope.entityId,
            type: targetScope.type,
            parentGroupId: targetScope.parentGroupId,
            name: group.name,
        });
        if (duplicate) {
            return { ok: false, reason: "duplicateName" };
        }
    }
    return { ok: true };
};

export const evaluateDrop = ({
    draggedRow,
    targetScope,
    groups,
    getRowCapabilities,
    accounts,
    destinationChainDepth,
    skipDuplicateName,
}: EvaluateDropParams): DropEvaluation => {
    const groupsById = new Map(groups.map((group) => [group.id, group]));
    if (draggedRow.kind === "account") {
        return evaluateAccountDrop({
            draggedRow,
            targetScope,
            groupsById,
            getRowCapabilities,
            destinationChainDepth,
            accounts,
            skipDuplicateName,
        });
    }
    if (draggedRow.kind === "group") {
        return evaluateGroupDrop({
            draggedRow,
            targetScope,
            groups,
            groupsById,
            accounts,
            getRowCapabilities,
            destinationChainDepth,
            skipDuplicateName,
        });
    }
    return { ok: true };
};

// Type headers have empty accounts/groups; destination business is the dragged row's.
const draggedRowEntityIds = (
    draggedRow: NestedEditorRow | undefined,
): number[] => {
    if (draggedRow == null) {
        return [];
    }
    if (draggedRow.kind === "account") {
        const fromConstituents = uniq(
            draggedRow.accounts.map((account) => account.entityId),
        );
        if (fromConstituents.length > 0) {
            return fromConstituents;
        }
        return draggedRow.account != null ? [draggedRow.account.entityId] : [];
    }
    if (draggedRow.kind === "group") {
        const fromConstituents = uniq(
            draggedRow.groups.map((group) => group.entityId),
        );
        if (fromConstituents.length > 0) {
            return fromConstituents;
        }
        return draggedRow.group != null ? [draggedRow.group.entityId] : [];
    }
    return [];
};

export const deriveDropTarget = ({
    overRow,
    dropPosition,
    draggedRow,
}: {
    overRow: NestedEditorRow;
    dropPosition: RowDropPosition;
    draggedRow?: NestedEditorRow;
}): DropTarget | null => {
    if (overRow.kind === "account" && overRow.account) {
        return {
            scope: {
                entityId: overRow.account.entityId,
                type: overRow.account.type,
                parentGroupId: overRow.account.parentGroupId,
            },
            overRefId: overRow.id,
            position: dropPosition === "before" ? "before" : "after",
        };
    }
    if (overRow.kind === "group" && overRow.group) {
        if (dropPosition === "on") {
            return {
                scope: {
                    entityId: overRow.group.entityId,
                    type: overRow.group.type,
                    parentGroupId: overRow.group.id,
                },
                overRefId: null,
                position: "after",
            };
        }
        return {
            scope: {
                entityId: overRow.group.entityId,
                type: overRow.group.type,
                parentGroupId: overRow.group.parentGroupId,
            },
            overRefId: overRow.group.id,
            position: dropPosition,
        };
    }
    if (overRow.kind === "type" && overRow.accountType) {
        return {
            scope: {
                entityId: draggedRowEntityIds(draggedRow)[0] ?? 0,
                type: overRow.accountType,
                parentGroupId: null,
            },
            overRefId: null,
            position: "after",
        };
    }
    return null;
};

// False hard-blocks; it does not redirect to a sibling.
export const isNestedRowDroppable = (row: NestedEditorRow): boolean =>
    deriveDropTarget({ overRow: row, dropPosition: "after" }) !== null;

export const isDropAllowed = ({
    draggedRow,
    overRow,
    dropPosition,
    groups,
    accounts,
    getRowCapabilities,
}: {
    draggedRow: NestedEditorRow;
    overRow: NestedEditorRow;
    dropPosition: RowDropPosition;
    groups: DraftGroup[];
    accounts: DraftAccount[];
    getRowCapabilities: GetRowCapabilities;
}): boolean => {
    if (draggedRow.kind !== "account" && draggedRow.kind !== "group") {
        return false;
    }
    const target = deriveDropTarget({ overRow, dropPosition, draggedRow });
    if (!target) {
        return false;
    }
    // Type header: one check per dragged business so a non-seed collision still blocks.
    const entityIds =
        overRow.kind === "type" ? draggedRowEntityIds(draggedRow) : [];
    const scopedEntityIds =
        entityIds.length > 0 ? entityIds : [target.scope.entityId];
    return scopedEntityIds.every(
        (entityId) =>
            evaluateDrop({
                draggedRow,
                targetScope: { ...target.scope, entityId },
                groups,
                accounts,
                getRowCapabilities,
            }).ok,
    );
};
